package com.banyan.flightviewer.web.state

import com.banyan.flightviewer.state.EnrichedFlightData
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.launch

/**
 * Wires flight-selection changes to FR24 enrichment + track fetching.
 *
 * Without this wrapper, clicking an aircraft on the web only sets
 * `appState.selectedCallsign` -- nothing fetches flight details (so the
 * route/ETA can stay blank) and nothing fetches historical track points
 * (so the flight path polyline never appears).
 *
 * The desktop equivalent is [FlightSelectionManager.selectFlight()](shared/src/commonMain/kotlin/com/banyan/flightviewer/manager/FlightSelectionManager.kt),
 * but that class constructs its own `Fr24Client` with an API key -- a
 * desktop pattern. On the web we reuse the existing server proxy via
 * [com.banyan.flightviewer.web.api.ServerClient].
 *
 * Called once from [com.banyan.flightviewer.web.Main.kt] on app startup.
 */
fun wireFlightTrackLoader(app: WebAppContainer) {
    var currentJob: Job? = null

    // Use drop(1) to skip the initial null emission -- the flow holds `null`
    // at construction time, no point running the "clear tracks" path before
    // anything has been selected.
    app.appState.scope.launch {
        app.appState.selectedCallsign.drop(1).collect { hex ->
            currentJob?.cancel()
            // Clear per-selection state up front, before any fetch, so the
            // DetailPanel doesn't flash the previous aircraft's photo /
            // tracks while the new ones load. `_selectedAircraftPhoto`
            // being non-null short-circuits the DetailPanel's fetch branch
            // (rendering the old photo) -- explicitly nulling it here is
            // what stops every click from showing whoever we last looked
            // at.
            app.appState.setSelectedAircraftPhoto(null)
            app.appState.setSelectedFlightTracks(emptyList())
            if (hex == null) return@collect
            currentJob = launchLoadFor(app, this, hex)
        }
    }
}

private fun launchLoadFor(app: WebAppContainer, scope: CoroutineScope, hex: String): Job =
    scope.launch {
        val callsign = app.appState.aircraftList.value.firstOrNull { it.hex == hex }?.callsign
            ?: return@launch

        // Step 1: enrich (so we have an fr24Id for the track fetch). Skip if
        // we already have non-empty enrichment for this hex.
        var enriched = app.appState.cachedEnrichedData.value[hex]
        if (enriched == null || enriched.fr24Id.isBlank()) {
            val fresh = app.serverClient.getFlightDetails(callsign)
            if (fresh != null) {
                enriched = mergeEnrichment(enriched, fresh)
                app.appState.cacheEnrichedData(hex, enriched)
            }
        }

        // Step 2: fetch tracks. Try fr24Id first; fall back to hex if FR24
        // couldn't resolve the callsign (the server proxy returns [] in
        // that case, so the path layer just stays empty).
        val trackId = enriched?.fr24Id?.takeIf { it.isNotBlank() } ?: hex
        val tracks = app.serverClient.getFlightTracks(trackId)
        // Guard against race: a later selection may have arrived while we
        // were awaiting the fetch.
        if (app.appState.selectedCallsign.value == hex) {
            app.appState.setSelectedFlightTracks(tracks)
        }
    }

/**
 * Merge two EnrichedFlightData instances, preferring fresh non-blank
 * fields over stale ones. Matches the fallback merge pattern used in
 * [com.banyan.flightviewer.web.components.DetailPanel.kt].
 */
private fun mergeEnrichment(stale: EnrichedFlightData?, fresh: EnrichedFlightData): EnrichedFlightData {
    if (stale == null) return fresh
    return stale.copy(
        origin = fresh.origin.ifBlank { stale.origin },
        destination = fresh.destination.ifBlank { stale.destination },
        aircraftType = fresh.aircraftType.ifBlank { stale.aircraftType },
        registration = fresh.registration.ifBlank { stale.registration },
        fr24Id = fresh.fr24Id.ifBlank { stale.fr24Id },
        eta = fresh.eta.ifBlank { stale.eta },
        departureTime = fresh.departureTime.ifBlank { stale.departureTime },
        cachedAt = fresh.cachedAt,
    )
}
